import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  supabase, insertRow, updateRow, deleteRow,
} from '../../db/engine';
import { etiquetaEstadoPoliza } from '../../db/renovaciones';
import { useApp } from '../../context/AppContext';
import { abrirDocumento } from '../../utils/archivos';
import ClienteSelect from '../../components/ClienteSelect';
import {
  Plus, Edit2, Trash2, Eye, X, User, Building,
  FileText, ExternalLink, Upload, Sparkles, Search,
} from 'lucide-react';
import DocManager from '../../components/DocManager';
import PolizaVistaModal from '../../components/PolizaVistaModal';
import { fmtFecha } from '../../utils/fecha';
import { ofrecerPaqueteIA } from '../../utils/iaCupo';
import { postIA } from '../../utils/apiIA';
import { PROVINCIAS, getDistritos, getCorregimientos } from '../../data/panamaGeo';

// Mantiene el valor guardado aunque no esté en el catálogo (datos viejos de texto
// libre): lo agrega como primera opción para no perderlo al editar.
const conActual = (lista, actual) => (actual && !lista.includes(actual)) ? [actual, ...lista] : lista;

const TIPO_DOC = [
  { value: 'cedula',      label: 'Cédula' },
  { value: 'licencia',    label: 'Licencia de Conducir' },
  { value: 'ctc_natural', label: 'Form. Conoce Tu Cliente' },
  { value: 'comprobante', label: 'Comprobante' },
  { value: 'otro',        label: 'Otro' },
];

const EMPTY = {
  tipo: 'natural',
  primer_nombre: '', segundo_nombre: '', primer_apellido: '', segundo_apellido: '', apellido_casada: '',
  nombre: '',
  cedula_ruc: '', tipo_identificacion: '', email: '', telefono: '', whatsapp: '',
  fecha_nacimiento: '', fecha_expedicion_doc: '', fecha_expiracion_doc: '',
  genero: '', ocupacion: '',
  direccion: '', provincia: '', distrito: '', corregimiento: '',
  referido_por: '', estado_cliente: 'activo', notas: '',
  correo_secundario: '',
  contacto_nombre: '', contacto_telefono: '', contacto_email: '',
  contacto_cobro_nombre: '', contacto_cobro_email: '', contacto_cobro_telefono: '',
  grupo_economico_id: '',
};

function buildNombre(f) {
  if (f.tipo === 'juridica') return f.nombre;
  return [f.primer_nombre, f.segundo_nombre, f.primer_apellido, f.segundo_apellido, f.apellido_casada]
    .filter(Boolean).join(' ');
}

function tipoLabel(v) {
  return TIPO_DOC.find(o => o.value === v)?.label || v;
}

export default function Clientes() {
  const { canEdit, agenteScope, esAgenteRestringido } = useApp();
  const [clientes, setClientes]           = useState([]);
  const [filterCliente, setFilterCliente] = useState('');
  const [modal, setModal]                 = useState(null);
  const [selected, setSelected]           = useState(null);
  const [form, setForm]                   = useState(EMPTY);
  const [polizasCliente, setPolizasCliente] = useState([]);
  const [polizaVer, setPolizaVer]         = useState(null); // póliza abierta como capa encima de la ficha del cliente
  const [loading, setLoading]             = useState(false);
  const [uploading, setUploading]         = useState(false);
  const [activeTab, setActiveTab]         = useState('datos');
  const [pendingDocs, setPendingDocs]     = useState([]);
  const [clienteTipos, setClienteTipos]   = useState([]);
  const pendingDocFileRef                 = useRef(null);
  const cedulaIAFileRef                   = useRef(null);
  const [extrayendoIA, setExtrayendoIA]   = useState(false);
  const [dupCedula, setDupCedula]         = useState(null); // cliente existente con el MISMO número de identificación (bloquea crear)
  const [dupNombre, setDupNombre]         = useState(null); // cliente existente con el MISMO nombre (solo avisa)
  const [volverAModal, setVolverAModal]   = useState(null); // al cerrar la ficha del duplicado, volver a 'create'/'edit' sin perder datos
  const [existingDocs, setExistingDocs]   = useState([]);
  const [viewDocs, setViewDocs]           = useState([]);
  const [docTipo, setDocTipo]             = useState('cedula');
  const [docComentario, setDocComentario] = useState('');
  const [grupos, setGrupos]              = useState([]);
  const fileRef = useRef();

  useEffect(() => {
    supabase.from('grupos_economicos').select('id,nombre').eq('activo',true).order('nombre')
      .then(({ data }) => setGrupos(data || []));
    supabase.from('tipos_documento').select('*').eq('activo',true)
      .in('categoria',['cliente_natural','cliente_juridico','otro']).order('orden').order('nombre')
      .then(({ data }) => setClienteTipos(data || []));
  }, []);

  // Chequeo de duplicados EN VIVO (mientras se escribe): por número de identificación
  // (bloquea crear) y por nombre / razón social (solo avisa). Debounce 500ms.
  useEffect(() => {
    if (modal !== 'create' && modal !== 'edit') { setDupCedula(null); setDupNombre(null); return; }
    const ced = (form.cedula_ruc || '').trim();
    const nom = buildNombre(form).trim();
    const excl = String(modal === 'edit' ? (selected?.id ?? '') : '');
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        if (ced) {
          const { data } = await supabase.from('clientes').select('id, numero_cliente, nombre, cedula_ruc').eq('cedula_ruc', ced);
          const hit = (data || []).find(c => String(c.id) !== excl);
          if (!cancel) setDupCedula(hit || null);
        } else if (!cancel) setDupCedula(null);
        if (nom) {
          const { data } = await supabase.from('clientes').select('id, numero_cliente, nombre, cedula_ruc').ilike('nombre', nom);
          const hit = (data || []).find(c => String(c.id) !== excl);
          if (!cancel) setDupNombre(hit || null);
        } else if (!cancel) setDupNombre(null);
      } catch { /* silencioso */ }
    }, 500);
    return () => { cancel = true; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cedula_ruc, form.tipo, form.primer_nombre, form.segundo_nombre, form.primer_apellido, form.segundo_apellido, form.apellido_casada, form.nombre, modal, selected?.id]);

  const load = useCallback(async () => {
    if (esAgenteRestringido && !agenteScope) return;
    // Búsqueda primero: sin cliente seleccionado no se trae ni se muestra la lista
    // (evita cargar TODOS los clientes de una vez).
    if (!filterCliente) { setClientes([]); setLoading(false); return; }
    setLoading(true);
    let q = supabase.from('clientes').select('*, grupos_economicos(nombre)').order('nombre');
    // Agente restringido: solo sus clientes
    if (esAgenteRestringido) q = q.in('id', agenteScope?.clienteIds?.length ? agenteScope.clienteIds : [-1]);
    let { data } = await q;
    // Filtro por selección (ClienteSelect): la lista solo se filtra al elegir un cliente.
    if (filterCliente) data = (data || []).filter(c => String(c.id) === String(filterCliente));
    const ids = (data || []).map(c => c.id);
    let counts = {};
    if (ids.length) {
      const { data: pols } = await supabase.from('polizas').select('cliente_id').eq('estado', 'activa').in('cliente_id', ids);
      (pols || []).forEach(p => { counts[p.cliente_id] = (counts[p.cliente_id] || 0) + 1; });
    }
    setClientes((data || []).map(c => ({ ...c, total_polizas: counts[c.id] || 0 })));
    setLoading(false);
  }, [filterCliente, esAgenteRestringido, agenteScope]);

  useEffect(() => { load(); }, [load]);

  const resetDocs = () => {
    setPendingDocs([]);
    setExistingDocs([]);
    setDocTipo('cedula');
    setDocComentario('');
  };

  const openCreate = () => {
    setForm(EMPTY);
    resetDocs();
    setActiveTab('datos');
    setModal('create');
  };

  const openEdit = async (c) => {
    setSelected(c);
    setForm({
      tipo:              c.tipo             || 'natural',
      primer_nombre:     c.primer_nombre    || '',
      segundo_nombre:    c.segundo_nombre   || '',
      primer_apellido:   c.primer_apellido  || '',
      segundo_apellido:  c.segundo_apellido || '',
      apellido_casada:   c.apellido_casada  || '',
      nombre:            c.nombre           || '',
      cedula_ruc:        c.cedula_ruc       || '',
      tipo_identificacion: c.tipo_identificacion || '',
      email:             c.email            || '',
      telefono:          c.telefono         || '',
      whatsapp:          c.whatsapp         || '',
      fecha_nacimiento:  c.fecha_nacimiento || '',
      fecha_expedicion_doc: c.fecha_expedicion_doc || '',
      fecha_expiracion_doc: c.fecha_expiracion_doc || '',
      genero:            c.genero           || '',
      ocupacion:         c.ocupacion        || '',
      direccion:         c.direccion        || '',
      provincia:         c.provincia        || '',
      distrito:          c.distrito         || '',
      corregimiento:     c.corregimiento    || '',
      referido_por:      c.referido_por     || '',
      estado_cliente:    c.estado_cliente   || 'activo',
      notas:             c.notas            || '',
      correo_secundario: c.correo_secundario || '',
      contacto_nombre:   c.contacto_nombre   || '',
      contacto_telefono: c.contacto_telefono || '',
      contacto_email:    c.contacto_email    || '',
      contacto_cobro_nombre:   c.contacto_cobro_nombre   || '',
      contacto_cobro_email:    c.contacto_cobro_email    || '',
      contacto_cobro_telefono: c.contacto_cobro_telefono || '',
      grupo_economico_id: c.grupo_economico_id ? String(c.grupo_economico_id) : '',
    });
    resetDocs();
    setActiveTab('datos');
    setModal('edit');
  };

  const openView = async (c) => {
    setSelected(c);
    const polResult = await supabase.from('polizas')
      .select('*, ramos(nombre), aseguradoras(nombre)')
      .eq('cliente_id', c.id)
      .order('fecha_vencimiento', { ascending: false });
    setPolizasCliente((polResult.data || []).map(p => ({
      ...p, ramo: p.ramos?.nombre, aseguradora: p.aseguradoras?.nombre,
    })));
    setModal('view');
  };

  // Abre la ficha del cliente duplicado trayendo su registro COMPLETO (la búsqueda
  // de duplicados solo trae una proyección mínima).
  const verClienteDup = async (c) => {
    // Solo desde creación permitimos "volver" (para no perder lo que cargó la IA).
    setVolverAModal(modal === 'create' ? 'create' : null);
    const { data: full } = await supabase.from('clientes').select('*').eq('id', c.id).maybeSingle();
    await openView(full || c);
  };

  const closeModal = () => {
    if (volverAModal) {
      // Se estaba revisando el cliente duplicado: volver al formulario con los datos
      // de la IA intactos (NO se resetea el form ni los documentos adjuntados).
      setVolverAModal(null);
      setSelected(null); setViewDocs([]); setPolizasCliente([]);
      setActiveTab('datos');
      setModal(volverAModal);
      return;
    }
    setModal(null); setSelected(null); setForm(EMPTY);
    resetDocs(); setViewDocs([]);
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Prepara el archivo para la IA: imágenes se reescalan a máx 1600px y van como
  // JPEG (más liviano/confiable); los PDF se mandan tal cual. Devuelve {media_type,data}.
  const fileADatosIA = (file) => new Promise((resolve, reject) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          const max = 1600;
          let { width, height } = img;
          if (width > max || height > max) {
            const r = Math.min(max / width, max / height);
            width = Math.round(width * r); height = Math.round(height * r);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve({ media_type: 'image/jpeg', data: canvas.toDataURL('image/jpeg', 0.85).split(',')[1] });
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    } else {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => resolve({ media_type: file.type || 'application/pdf', data: String(reader.result).split(',')[1] });
      reader.readAsDataURL(file);
    }
  });

  // Lee un documento con IA (cédula = persona natural; registro público / aviso de
  // operaciones = jurídica), verifica que el cliente no exista ya, autocompleta los
  // campos y adjunta el mismo archivo a los documentos. La persona revisa antes de guardar.
  const extraerDocumentoIA = async (file) => {
    if (!file) return;
    const esJuridica = form.tipo === 'juridica';
    setExtrayendoIA(true);
    try {
      const { media_type, data } = await fileADatosIA(file);
      const pedir = async () => {
        return await postIA('/api/extraer-documento', { tipo: esJuridica ? 'juridica' : 'cedula', media_type, data });
      };
      let res = await pedir();
      // Cupo agotado → se ofrece ampliarlo de un clic y, si acepta, se reintenta
      // solo (no se le pide al usuario que vuelva a subir el documento).
      if (!res.ok && res.reason === 'cupo' && await ofrecerPaqueteIA(res.detalle)) {
        res = await pedir();
      }
      if (!res.ok) {
        // El mensaje SIEMPRE debe decir POR QUÉ falló. `detalle` trae el motivo del
        // servidor (ej. el aviso de cupo agotado del proxy de IA), y se muestra tal cual.
        const motivos = {
          no_configurado: 'La extracción con IA aún no está activada en tu sistema.',
          cupo: res.detalle || 'Llegaste al límite de análisis con IA de tu plan este mes.',
          api_error: 'El servicio de IA devolvió un error. Intenta de nuevo.',
          sin_json: 'No se pudieron leer datos claros. Prueba con una foto/archivo más nítido.',
        };
        const base = motivos[res.reason] || 'No se pudo leer el documento.';
        const extra = (res.reason !== 'cupo' && res.detalle) ? `\n\nDetalle: ${res.detalle}` : '';
        alert(base + extra);
        return;
      }
      const d = res.datos || {};
      const set = (k, v) => { if (v != null && String(v).trim() !== '') setField(k, String(v).trim()); };
      let defTipoRegex;
      if (esJuridica) {
        setField('tipo', 'juridica');
        set('nombre', d.nombre);
        set('cedula_ruc', d.cedula_ruc);
        set('direccion', d.direccion);
        set('email', d.email);
        set('telefono', d.telefono);
        setField('tipo_identificacion', 'RUC');
        defTipoRegex = /registro|aviso|ruc|operaci/i;
      } else {
        setField('tipo', 'natural');
        set('primer_nombre', d.primer_nombre);
        set('segundo_nombre', d.segundo_nombre);
        set('primer_apellido', d.primer_apellido);
        set('segundo_apellido', d.segundo_apellido);
        set('apellido_casada', d.apellido_casada);
        set('cedula_ruc', d.cedula_ruc);
        set('fecha_nacimiento', d.fecha_nacimiento);
        set('fecha_expedicion_doc', d.fecha_expedicion_doc);
        set('fecha_expiracion_doc', d.fecha_expiracion_doc);
        if (d.genero) setField('genero', /fem/i.test(d.genero) ? 'FEMENINO' : 'MASCULINO');
        setField('tipo_identificacion', 'Cedula');
        defTipoRegex = /c[eé]dula/i;
      }

      // Adjunta el mismo archivo como documento del cliente (sin re-subirlo).
      let docAgregado = false;
      if (modal === 'create') {
        const cats = esJuridica ? ['cliente_juridico','otro'] : ['cliente_natural','otro'];
        const catTipos = (clienteTipos || []).filter(t => cats.includes(t.categoria));
        const tipoDef = catTipos.find(t => defTipoRegex.test(t.nombre || ''));
        setPendingDocs(prev => [...prev, {
          file, url: URL.createObjectURL(file),
          tipo_id: tipoDef ? String(tipoDef.id) : '',
          requiere_anio: !!tipoDef?.requiere_anio,
          anio: new Date().getFullYear(),
        }]);
        docAgregado = true;
      }

      // Los datos quedaron cargados. Si el cliente ya existe (por identificación o
      // nombre), el chequeo EN VIVO lo mostrará en el banner de abajo — sin perder lo
      // cargado, y con el N° clicable para revisar el existente.
      alert('✅ Datos extraídos.' +
        (docAgregado ? '\n\nEl documento quedó agregado en la pestaña "Documentos" — confirma o cambia el tipo.' : '') +
        '\n\nRevisa los datos antes de guardar. Si aparece un aviso de duplicado abajo, revísalo.');
    } catch (e) {
      alert('Error al procesar el documento: ' + (e.message || e));
    } finally {
      setExtrayendoIA(false);
      if (cedulaIAFileRef.current) cedulaIAFileRef.current.value = '';
    }
  };

  const handleSave = async (esBorrador = false) => {
    const nombre = buildNombre(form);
    // Nombre mínimo (siempre — también en borrador, para identificar al cliente).
    if (form.tipo === 'natural') {
      if (!form.primer_nombre.trim()) return alert(esBorrador ? 'Al menos el primer nombre es requerido para guardar el borrador.' : 'El primer nombre es requerido.');
    } else {
      if (!form.nombre.trim()) return alert('La razón social es requerida.');
    }
    // Campos obligatorios (solo al CREAR completo; no en borrador ni al editar, para
    // no bloquear la corrección de clientes viejos que aún no tienen esos datos).
    if (!esBorrador && modal === 'create') {
      const faltan = [];
      const emailOk = (v) => /^\S+@\S+\.\S+$/.test((v || '').trim());
      if (form.tipo === 'natural') {
        if (!form.primer_apellido.trim()) faltan.push('Primer apellido');
        if (!form.email.trim())           faltan.push('Correo electrónico');
        else if (!emailOk(form.email))    faltan.push('Correo electrónico (formato inválido)');
        if (!form.whatsapp.trim())        faltan.push('Celular / número de contacto');
      } else { // jurídica: obligatorios de la PERSONA DE CONTACTO
        if (!form.contacto_nombre.trim())    faltan.push('Nombre de la persona de contacto');
        if (!form.contacto_telefono.trim())  faltan.push('Teléfono del contacto');
        if (!form.contacto_email.trim())     faltan.push('Correo del contacto');
        else if (!emailOk(form.contacto_email)) faltan.push('Correo del contacto (formato inválido)');
      }
      if (!form.tipo_identificacion)  faltan.push('Tipo de identificación');
      if (!form.cedula_ruc.trim())    faltan.push('Número de identificación');
      if (!form.provincia)            faltan.push('Provincia');
      if (!form.distrito)             faltan.push('Distrito');
      if (!form.corregimiento)        faltan.push('Corregimiento');
      if (faltan.length) return alert('Faltan campos obligatorios:\n\n• ' + faltan.join('\n• '));
    }
    // No puede haber DOS clientes con el mismo número de identificación: al guardar
    // COMPLETO (Crear / Guardar Cambios) se bloquea. En BORRADOR sí se permite (para
    // aclarar o corregir). El nombre duplicado NO bloquea (solo avisa en el banner).
    if (!esBorrador) {
      const ced = form.cedula_ruc.trim();
      if (ced) {
        const { data } = await supabase.from('clientes').select('id, numero_cliente, nombre').eq('cedula_ruc', ced);
        const excl = String(modal === 'edit' ? (selected?.id ?? '') : '');
        const hit = (data || []).find(c => String(c.id) !== excl);
        if (hit) return alert(`No se puede guardar: el número de identificación "${ced}" ya está registrado en el cliente N° ${hit.numero_cliente || hit.id} (${hit.nombre}).\n\nSi necesitas aclararlo, usa "Guardar Borrador".`);
      }
    }

    try {
      setUploading(true);
      const payload = { ...form, nombre,
        grupo_economico_id: form.grupo_economico_id ? parseInt(form.grupo_economico_id) : null,
      };
      if (!payload.fecha_nacimiento) delete payload.fecha_nacimiento;

      let clienteId;
      let nuevo = null;
      if (modal === 'create') {
        nuevo = await insertRow('clientes', payload);
        clienteId = nuevo.id;
      } else {
        await updateRow('clientes', selected.id, { ...payload, actualizado_en: new Date().toISOString() });
        clienteId = selected.id;
      }

      // Subir documentos adjuntados durante la creación (van a la tabla `documentos`,
      // misma que usa DocManager, por lo que se ven luego en el módulo del cliente).
      if (pendingDocs.length > 0 && clienteId) {
        for (const pd of pendingDocs) {
          try {
            const ext  = pd.file.name.split('.').pop();
            const path = `documentos/cliente/${clienteId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
            const { error } = await supabase.storage.from('solicitud-archivos').upload(path, pd.file, { upsert:false });
            if (!error) {
              await insertRow('documentos', {
                tipo_documento_id: pd.tipo_id ? parseInt(pd.tipo_id) : null,
                nombre_archivo: pd.file.name,
                storage_path: path,
                anio: pd.requiere_anio && pd.anio ? parseInt(pd.anio) : null,
                activo: true,
                cliente_id: clienteId,
              });
            }
          } catch {}
        }
        setPendingDocs([]);
      }

      // Mensaje de éxito con el N° de cliente (se re-consulta si la BD lo asigna por trigger).
      const fueCreacion = modal === 'create';
      let numCli = fueCreacion ? nuevo?.numero_cliente : selected?.numero_cliente;
      if (fueCreacion && !numCli && clienteId) {
        const { data: ref } = await supabase.from('clientes').select('numero_cliente').eq('id', clienteId).maybeSingle();
        numCli = ref?.numero_cliente || null;
      }

      await load();
      closeModal();
      const accion = fueCreacion ? (esBorrador ? 'guardado como borrador' : 'creado') : 'actualizado';
      alert(`✅ Cliente ${accion} satisfactoriamente.` + (numCli ? `\n\nN° de Cliente: ${numCli}` : ''));
    } catch (e) {
      alert('Error al guardar: ' + (e.message || ''));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar a ${c.nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteRow('clientes', c.id);
      await load();
    } catch {
      alert('No se puede eliminar — el cliente tiene pólizas asociadas.');
    }
  };

  const docCount = 0; // Conteo gestionado por DocManager

  // Aviso de duplicado reutilizable (se muestra arriba —tras autocompletar— y abajo,
  // sobre los botones). N° clicable para abrir la ficha del existente.
  const dupBannerInner = dupCedula ? (
    <div style={{ padding:'8px 12px', background:'rgba(239, 68, 68, 0.1)', border:'1px solid rgba(239, 68, 68, 0.4)', borderRadius:8, fontSize:12, color:'var(--text-danger)' }}>
      ⚠️ <strong>Número de identificación duplicado</strong> con el cliente{' '}
      <button type="button" onClick={() => verClienteDup(dupCedula)} style={{ background:'none', border:'none', color:'var(--sky-light)', cursor:'pointer', textDecoration:'underline', fontWeight:700, padding:0, fontFamily:'inherit', fontSize:12 }}>N° {dupCedula.numero_cliente || dupCedula.id}</button>. No se puede crear; puedes <strong>Guardar Borrador</strong> para aclararlo.
    </div>
  ) : dupNombre ? (
    <div style={{ padding:'8px 12px', background:'rgba(245, 158, 11, 0.1)', border:'1px solid rgba(245, 158, 11, 0.4)', borderRadius:8, fontSize:12, color:'var(--text-warning)' }}>
      ⚠️ <strong>Nombre duplicado</strong> con el cliente{' '}
      <button type="button" onClick={() => verClienteDup(dupNombre)} style={{ background:'none', border:'none', color:'var(--sky-light)', cursor:'pointer', textDecoration:'underline', fontWeight:700, padding:0, fontFamily:'inherit', fontSize:12 }}>N° {dupNombre.numero_cliente || dupNombre.id}</button> (identificación distinta). Puedes guardar igual.
    </div>
  ) : null;

  return (
    <div>
      {/* ── Encabezado ── */}
      <div className="page-header">
        <div>
          <div className="page-title">Clientes</div>
          <div className="page-subtitle">{loading ? 'Cargando...' : filterCliente ? `${clientes.length} registro(s)` : 'Busca un cliente para ver sus datos'}</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {canEdit('clientes') && (
            <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Nuevo Cliente</button>
          )}
        </div>
      </div>

      {/* ── Filtro por selección (estilo Morosidad): la lista solo se filtra al elegir ── */}
      <div className="toolbar">
        <ClienteSelect value={filterCliente} onChange={setFilterCliente} placeholder="Filtrar por cliente..." width={340} />
      </div>

      {/* ── Tabla ── */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>N° Cliente</th><th>Nombre</th><th>Tipo</th>
              <th>Cédula / RUC</th><th>Teléfono</th><th>Email</th>
              <th>Pólizas Activas</th><th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!filterCliente ? (
              <tr><td colSpan={8}>
                <div className="empty-state">
                  <div className="empty-state-icon"><Search size={48} /></div>
                  <div className="empty-state-title">Busca un cliente para verlo</div>
                  <div>Usa el buscador de arriba. La lista no muestra todos los clientes de una vez.</div>
                </div>
              </td></tr>
            ) : clientes.length === 0 ? (
              <tr><td colSpan={8}>
                <div className="empty-state">
                  <div className="empty-state-icon"><User size={48} /></div>
                  <div className="empty-state-title">Sin resultados</div>
                  <div>Ningún cliente coincide con la búsqueda.</div>
                </div>
              </td></tr>
            ) : clientes.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight:600, fontSize:12, color:'var(--sky)' }}>{c.numero_cliente || '—'}</td>
                <td style={{ fontWeight:600 }}>{c.nombre}</td>
                <td>
                  <span className={`badge ${c.tipo === 'natural' ? 'badge-info' : 'badge-neutral'}`}>
                    {c.tipo === 'natural' ? <><User size={10}/> Nat.</> : <><Building size={10}/> Jurídica</>}
                  </span>
                </td>
                <td className="text-silver">{c.cedula_ruc || '—'}</td>
                <td className="text-silver">{c.telefono || '—'}</td>
                <td className="text-silver">{c.email || '—'}</td>
                <td><span className={`badge ${c.total_polizas > 0 ? 'badge-success' : 'badge-neutral'}`}>{c.total_polizas || 0}</span></td>
                <td>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-icon btn-secondary btn-sm" onClick={() => openView(c)} title="Ver"><Eye size={14}/></button>
                    {canEdit('clientes') && <>
                      <button className="btn btn-icon btn-secondary btn-sm" onClick={() => openEdit(c)} title="Editar"><Edit2 size={14}/></button>
                      <button className="btn btn-icon btn-danger btn-sm" onClick={() => handleDelete(c)} title="Eliminar"><Trash2 size={14}/></button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ══════════════════════════════════════════
          Modal Crear / Editar
      ══════════════════════════════════════════ */}
      {(modal === 'create' || modal === 'edit') && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>

            <div className="modal-header">
              <div>
                <div className="modal-title">{modal === 'create' ? 'Nuevo Cliente' : 'Editar Cliente'}</div>
                {modal === 'edit' && selected?.numero_cliente && (
                  <div className="text-silver text-sm">{selected.numero_cliente}</div>
                )}
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={closeModal}><X size={16}/></button>
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', borderBottom:'1px solid var(--steel)', padding:'0 24px' }}>
              <TabBtn label="Datos Personales" active={activeTab === 'datos'} onClick={() => setActiveTab('datos')} />
              <TabBtn
                label={`Documentos${docCount > 0 ? ` (${docCount})` : ''}`}
                active={activeTab === 'docs'}
                onClick={() => setActiveTab('docs')}
              />
            </div>

            <div className="modal-body">

              {/* ── Tab: Datos ── */}
              {activeTab === 'datos' && (
                <div className="form-grid form-grid-2">
                  {/* Primero se elige el tipo de persona (define el autocompletar y el tipo de identificación) */}
                  <div className="form-group">
                    <label className="form-label">Tipo de Persona *</label>
                    <select className="form-select" value={form.tipo}
                      onChange={e => setForm(f => ({ ...f, tipo: e.target.value, tipo_identificacion: '' }))}>
                      <option value="natural">Persona Natural</option>
                      <option value="juridica">Persona Jurídica</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ display:'flex', alignItems:'flex-end', paddingBottom:6 }}>
                    {modal === 'edit' && selected?.numero_cliente && (
                      <span style={{ fontSize:12, color:'var(--silver)' }}>
                        N° Cliente: <strong style={{ color:'var(--sky)' }}>{selected.numero_cliente}</strong>
                      </span>
                    )}
                  </div>
                  <div className="form-group" style={{ gridColumn:'span 2' }}>
                    <input ref={cedulaIAFileRef} type="file" accept="image/*,application/pdf" style={{ display:'none' }}
                      onChange={e => { const f = e.target.files?.[0]; if (f) extraerDocumentoIA(f); }} />
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', padding:'10px 14px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.22)', borderRadius:10 }}>
                      <button type="button" className="btn btn-secondary btn-sm" disabled={extrayendoIA}
                        onClick={() => cedulaIAFileRef.current?.click()}>
                        <Sparkles size={14}/> {extrayendoIA ? 'Leyendo documento…' : (form.tipo === 'juridica' ? 'Autocompletar con registro público (IA)' : 'Autocompletar con cédula (IA)')}
                      </button>
                      <span style={{ fontSize:12, color:'var(--silver)' }}>
                        {form.tipo === 'juridica'
                          ? 'Sube el registro público / aviso de operaciones (foto o PDF) y la IA llena los datos. Revisa antes de guardar.'
                          : 'Sube una foto o PDF de la cédula y la IA llena los campos. Revisa antes de guardar.'}
                      </span>
                    </div>
                  </div>

                  {/* Aviso de duplicado arriba (tras autocompletar, antes de los datos) */}
                  {dupBannerInner && (
                    <div className="form-group" style={{ gridColumn:'span 2' }}>{dupBannerInner}</div>
                  )}

                  {form.tipo === 'natural' ? (
                    <>
                      <div className="form-group">
                        <label className="form-label">Primer Nombre *</label>
                        <input className="form-input" value={form.primer_nombre}
                          onChange={e => setField('primer_nombre', e.target.value)} placeholder="Ej: María" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Segundo Nombre</label>
                        <input className="form-input" value={form.segundo_nombre}
                          onChange={e => setField('segundo_nombre', e.target.value)} placeholder="Opcional" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Primer Apellido *</label>
                        <input className="form-input" value={form.primer_apellido}
                          onChange={e => setField('primer_apellido', e.target.value)} placeholder="Ej: Rodríguez" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Segundo Apellido</label>
                        <input className="form-input" value={form.segundo_apellido}
                          onChange={e => setField('segundo_apellido', e.target.value)} placeholder="Opcional" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Apellido de Casada</label>
                        <input className="form-input" value={form.apellido_casada}
                          onChange={e => setField('apellido_casada', e.target.value)} placeholder="Opcional" />
                      </div>
                      <div /> {/* spacer */}
                    </>
                  ) : (
                    <div className="form-group" style={{ gridColumn:'span 2' }}>
                      <label className="form-label">Razón Social *</label>
                      <input className="form-input" value={form.nombre}
                        onChange={e => setField('nombre', e.target.value)} placeholder="Nombre de la empresa" />
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Número de Identificación *</label>
                    <input className="form-input" value={form.cedula_ruc}
                      onChange={e => setField('cedula_ruc', e.target.value)} placeholder="Cédula, RUC, pasaporte..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo de Identificación *</label>
                    <select className="form-select" value={form.tipo_identificacion} onChange={e => setField('tipo_identificacion', e.target.value)}>
                      <option value="">Seleccionar...</option>
                      {form.tipo === 'juridica' ? (<>
                        <option value="RUC">RUC</option>
                        <option value="Aviso de Operacion">Aviso de Operación</option>
                      </>) : (<>
                        <option value="Cedula">Cédula</option>
                        <option value="Cedula de Extranjeria">Cédula de Extranjería</option>
                        <option value="Pasaporte">Pasaporte</option>
                      </>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">WhatsApp / Celular {form.tipo === 'natural' ? '*' : ''}</label>
                    <input className="form-input" value={form.whatsapp}
                      onChange={e => setField('whatsapp', e.target.value)} placeholder="+507 6000-0000" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono (fijo)</label>
                    <input className="form-input" value={form.telefono}
                      onChange={e => setField('telefono', e.target.value)} placeholder="220-0000" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Correo electrónico {form.tipo === 'natural' ? '*' : ''}</label>
                    <input className="form-input" type="email" value={form.email}
                      onChange={e => setField('email', e.target.value)} placeholder="correo@ejemplo.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Correo secundario</label>
                    <input className="form-input" type="email" value={form.correo_secundario}
                      onChange={e => setField('correo_secundario', e.target.value)} placeholder="correo2@ejemplo.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Grupo Económico</label>
                    <select className="form-select" value={form.grupo_economico_id} onChange={e => setField('grupo_economico_id', e.target.value)}>
                      <option value="">Sin grupo</option>
                      {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado del cliente</label>
                    <select className="form-select" value={form.estado_cliente} onChange={e => setField('estado_cliente', e.target.value)}>
                      <option value="activo">Activo</option>
                      <option value="potencial">Potencial</option>
                      <option value="perdido">Perdido</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                  {form.tipo === 'natural' && (<>
                    <div className="form-group">
                      <label className="form-label">Fecha de Nacimiento</label>
                      <input className="form-input" type="date" value={form.fecha_nacimiento}
                        onChange={e => setField('fecha_nacimiento', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Género</label>
                      <select className="form-select" value={form.genero} onChange={e => setField('genero', e.target.value)}>
                        <option value="">Seleccionar...</option>
                        <option value="MASCULINO">Masculino</option>
                        <option value="FEMENINO">Femenino</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Ocupación</label>
                      <input className="form-input" value={form.ocupacion}
                        onChange={e => setField('ocupacion', e.target.value)} placeholder="Ej: Ingeniero" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Fecha Expedición Documento</label>
                      <input className="form-input" type="date" value={form.fecha_expedicion_doc}
                        onChange={e => setField('fecha_expedicion_doc', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Fecha Vencimiento Documento</label>
                      <input className="form-input" type="date" value={form.fecha_expiracion_doc}
                        onChange={e => setField('fecha_expiracion_doc', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Referido por</label>
                      <input className="form-input" value={form.referido_por}
                        onChange={e => setField('referido_por', e.target.value)} placeholder="Nombre de quien lo refirió" />
                    </div>
                  </>)}
                  <div className="form-group">
                    <label className="form-label">Provincia *</label>
                    <select className="form-select" value={form.provincia}
                      onChange={e => { setField('provincia', e.target.value); setField('distrito',''); setField('corregimiento',''); }}>
                      <option value="">Seleccione...</option>
                      {conActual(PROVINCIAS, form.provincia).map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Distrito *</label>
                    <select className="form-select" value={form.distrito} disabled={!form.provincia}
                      onChange={e => { setField('distrito', e.target.value); setField('corregimiento',''); }}>
                      <option value="">{form.provincia ? 'Seleccione...' : 'Elija provincia primero'}</option>
                      {conActual(getDistritos(form.provincia), form.distrito).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn:'span 2' }}>
                    <label className="form-label">Corregimiento *</label>
                    <select className="form-select" value={form.corregimiento} disabled={!form.distrito}
                      onChange={e => setField('corregimiento', e.target.value)}>
                      <option value="">{form.distrito ? 'Seleccione...' : 'Elija distrito primero'}</option>
                      {conActual(getCorregimientos(form.provincia, form.distrito), form.corregimiento).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn:'span 2' }}>
                    <label className="form-label">Dirección</label>
                    <input className="form-input" value={form.direccion}
                      onChange={e => setField('direccion', e.target.value)} placeholder="Calle, casa/edificio, referencias..." />
                  </div>

                  {/* Persona de contacto — obligatoria para persona jurídica */}
                  {form.tipo === 'juridica' && (<>
                    <div className="form-group" style={{ gridColumn:'span 2', marginTop:4 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, textTransform:'uppercase' }}>Persona de Contacto *</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nombre del contacto *</label>
                      <input className="form-input" value={form.contacto_nombre}
                        onChange={e => setField('contacto_nombre', e.target.value)} placeholder="Nombre de la persona de contacto" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Teléfono del contacto *</label>
                      <input className="form-input" value={form.contacto_telefono}
                        onChange={e => setField('contacto_telefono', e.target.value)} placeholder="+507 6000-0000" />
                    </div>
                    <div className="form-group" style={{ gridColumn:'span 2' }}>
                      <label className="form-label">Correo del contacto *</label>
                      <input className="form-input" type="email" value={form.contacto_email}
                        onChange={e => setField('contacto_email', e.target.value)} placeholder="contacto@empresa.com" />
                    </div>
                  </>)}

                  {/* Contacto de cobros — opcional (ambos tipos) */}
                  <div className="form-group" style={{ gridColumn:'span 2', marginTop:4 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, textTransform:'uppercase' }}>Contacto de Cobros (opcional)</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre (cobros)</label>
                    <input className="form-input" value={form.contacto_cobro_nombre}
                      onChange={e => setField('contacto_cobro_nombre', e.target.value)} placeholder="Persona de contacto de cobros" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono (cobros)</label>
                    <input className="form-input" value={form.contacto_cobro_telefono}
                      onChange={e => setField('contacto_cobro_telefono', e.target.value)} placeholder="+507 6000-0000" />
                  </div>
                  <div className="form-group" style={{ gridColumn:'span 2' }}>
                    <label className="form-label">Correo (cobros)</label>
                    <input className="form-input" type="email" value={form.contacto_cobro_email}
                      onChange={e => setField('contacto_cobro_email', e.target.value)} placeholder="cobros@empresa.com" />
                  </div>
                  <div className="form-group" style={{ gridColumn:'span 2' }}>
                    <label className="form-label">Notas internas</label>
                    <textarea className="form-textarea" value={form.notas}
                      onChange={e => setField('notas', e.target.value)} placeholder="Notas relevantes..." />
                  </div>
                </div>
              )}

              {/* ── Tab: Documentos ── */}
              {activeTab === 'docs' && (
                <div>
                  {modal === 'edit' && selected?.id ? (
                    <DocManager
                      clienteId={selected.id}
                      tipoCliente={form.tipo}
                      categoriasPermitidas={form.tipo === 'natural' ? ['cliente_natural','otro'] : ['cliente_juridico','otro']}
                      readOnly={!canEdit('clientes')}
                    />
                  ) : (() => {
                    // En creación: adjuntar documentos pendientes (se suben al guardar el cliente).
                    const cats = form.tipo === 'natural' ? ['cliente_natural','otro'] : ['cliente_juridico','otro'];
                    const tiposFiltrados = clienteTipos.filter(t => cats.includes(t.categoria));
                    return (
                      <div>
                        <div className="text-silver text-sm" style={{ marginBottom:12 }}>
                          Adjunta los documentos del cliente. Se guardarán al crear el cliente.
                        </div>
                        <input ref={pendingDocFileRef} type="file" style={{ display:'none' }}
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) setPendingDocs(prev => [...prev, { file, url: URL.createObjectURL(file), tipo_id:'', requiere_anio:false, anio:new Date().getFullYear() }]);
                            if (pendingDocFileRef.current) pendingDocFileRef.current.value = '';
                          }} />
                        <button className="btn btn-secondary btn-sm" onClick={() => pendingDocFileRef.current?.click()} style={{ marginBottom:10 }}>
                          <Upload size={13}/> Adjuntar documento
                        </button>
                        {pendingDocs.length > 0 && (
                          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                            {pendingDocs.map((pd, i) => (
                              <div key={i} style={{ padding:'8px 12px', background:'rgba(var(--navy-rgb),0.4)', borderRadius:8, border:'1px solid rgba(var(--silver-rgb),0.1)' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                  <FileText size={13} style={{ color:'var(--sky-light)', flexShrink:0 }}/>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pd.file.name}</div>
                                  </div>
                                  <button className="btn btn-icon btn-danger btn-sm" onClick={() => setPendingDocs(prev => { const x = prev[i]; if (x?.url) URL.revokeObjectURL(x.url); return prev.filter((_,j)=>j!==i); })}><X size={12}/></button>
                                </div>
                                {/* Vista previa del documento antes de crear el cliente */}
                                {pd.url && (
                                  <div style={{ marginTop:8 }}>
                                    {pd.file.type.startsWith('image/') ? (
                                      <img src={pd.url} alt="Vista previa" style={{ maxWidth:'100%', maxHeight:220, borderRadius:8, border:'1px solid rgba(var(--silver-rgb),0.2)', display:'block' }} />
                                    ) : pd.file.type === 'application/pdf' ? (
                                      <iframe title="Vista previa" src={pd.url} style={{ width:'100%', height:260, border:'1px solid rgba(var(--silver-rgb),0.2)', borderRadius:8, background:'#fff' }} />
                                    ) : (
                                      <div style={{ fontSize:11, color:'var(--silver)' }}>Sin vista previa para este tipo de archivo.</div>
                                    )}
                                  </div>
                                )}
                                <div style={{ marginTop:8 }}>
                                  <label className="form-label" style={{ fontSize:11 }}>Tipo de documento *</label>
                                  <div style={{ display:'flex', gap:8 }}>
                                    <select className="form-select" style={{ flex:1 }} value={pd.tipo_id}
                                      onChange={e => { const val = e.target.value; setPendingDocs(prev => prev.map((x,j)=>{
                                        if (j!==i) return x;
                                        const t = tiposFiltrados.find(tt => String(tt.id)===String(val));
                                        return { ...x, tipo_id:val, requiere_anio: !!t?.requiere_anio };
                                      })); }}>
                                      <option value="">Seleccionar tipo...</option>
                                      {tiposFiltrados.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                                    </select>
                                    {pd.requiere_anio && (
                                      <input className="form-input" type="number" style={{ width:110 }} placeholder="Año"
                                        value={pd.anio} onChange={e => setPendingDocs(prev => prev.map((x,j)=>j===i?{...x,anio:e.target.value}:x))}/>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Aviso de duplicado (en vivo) arriba de los botones */}
            {dupBannerInner && <div style={{ padding:'10px 24px 0' }}>{dupBannerInner}</div>}

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
              {modal === 'create' && (
                <button className="btn btn-secondary" onClick={() => handleSave(true)} disabled={uploading}>
                  {uploading ? 'Guardando...' : 'Guardar Borrador'}
                </button>
              )}
              <button className="btn btn-primary" onClick={() => handleSave(false)} disabled={uploading || !!dupCedula}
                title={dupCedula ? 'No se puede crear: número de identificación duplicado. Usa Guardar Borrador.' : undefined}>
                {uploading ? 'Guardando...' : modal === 'create' ? 'Crear Cliente' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          Modal Ver
      ══════════════════════════════════════════ */}
      {modal === 'view' && selected && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{selected.nombre}</div>
                <div className="text-silver text-sm">
                  {selected.tipo === 'natural' ? 'Persona Natural' : 'Persona Jurídica'}
                  {selected.numero_cliente && (
                    <span style={{ color:'var(--sky)', marginLeft:8, fontWeight:700 }}>{selected.numero_cliente}</span>
                  )}
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={closeModal}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-3" style={{ marginBottom:24 }}>
                <InfoField label="Cédula / RUC"     value={selected.cedula_ruc} />
                <InfoField label="Tipo Identificación" value={selected.tipo_identificacion} />
                <InfoField label="Estado"            value={selected.estado_cliente} />
                <InfoField label="WhatsApp / Celular" value={selected.whatsapp} />
                <InfoField label="Teléfono"          value={selected.telefono} />
                <InfoField label="Email"             value={selected.email} />
                {selected.tipo === 'natural' && <>
                  <InfoField label="Fecha de Nacimiento" value={fmtFecha(selected.fecha_nacimiento)} />
                  <InfoField label="Género"           value={selected.genero} />
                  <InfoField label="Ocupación"        value={selected.ocupacion} />
                  {selected.apellido_casada && <InfoField label="Apellido de Casada" value={selected.apellido_casada} />}
                  <InfoField label="Vence documento"  value={fmtFecha(selected.fecha_expiracion_doc)} />
                  <InfoField label="Referido por"     value={selected.referido_por} />
                </>}
                <InfoField label="Dirección" value={selected.direccion} style={{ gridColumn:'span 3' }} />
                {(selected.provincia||selected.distrito||selected.corregimiento) && (
                  <InfoField label="Ubicación"
                    value={[selected.provincia, selected.distrito, selected.corregimiento].filter(Boolean).join(', ')}
                    style={{ gridColumn:'span 3' }} />
                )}
              </div>

              {selected.notas && (
                <div className="card card-sm" style={{ marginBottom:20 }}>
                  <div className="text-silver text-xs" style={{ marginBottom:4 }}>NOTAS</div>
                  <div style={{ fontSize:14 }}>{selected.notas}</div>
                </div>
              )}

              {/* Documentos */}
              <div style={{ marginBottom:24 }}>
                <div style={{ fontSize:15, fontWeight:700, marginBottom:12 }}>Documentos</div>
                <DocManager
                  clienteId={selected.id}
                  tipoCliente={selected.tipo}
                  categoriasPermitidas={selected.tipo === 'natural' ? ['cliente_natural','otro'] : ['cliente_juridico','otro']}
                  readOnly={!canEdit('clientes')}
                />
              </div>

              {/* Pólizas */}
              <div style={{ fontSize:15, fontWeight:700, marginBottom:12 }}>Pólizas ({polizasCliente.length})</div>
              {polizasCliente.length > 0 ? (
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr><th>Número</th><th>Ramo</th><th>Aseguradora</th><th>Prima</th><th>Vencimiento</th><th>Estado</th></tr>
                    </thead>
                    <tbody>
                      {polizasCliente.map(p => (
                        <tr key={p.id} onClick={() => setPolizaVer(p.id)} style={{ cursor:'pointer' }}
                          title="Ver detalle de la póliza">
                          <td className="font-bold text-sky" style={{ textDecoration:'underline' }}>{p.numero}</td>
                          <td>{p.ramo}</td>
                          <td className="text-silver">{p.aseguradora}</td>
                          <td className="text-success">${Number(p.prima).toLocaleString('es-PA')}</td>
                          <td className="text-silver">{fmtFecha(p.fecha_vencimiento)}</td>
                          <td><span className={`badge badge-${p.estado === 'activa' ? 'success' : 'neutral'}`}>{etiquetaEstadoPoliza(p.estado)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="text-silver text-sm">Sin pólizas registradas</div>}
            </div>
          </div>
        </div>
      )}

      {/* Detalle de póliza como capa encima de la ficha del cliente — al cerrar
          se vuelve a la misma pantalla (filtros intactos), sin ir al módulo de Pólizas. */}
      <PolizaVistaModal polizaId={polizaVer} onClose={() => setPolizaVer(null)} />
    </div>
  );
}

// ── Componentes auxiliares ──────────────────────────

function TabBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding:'10px 16px', fontSize:13, fontWeight:600,
      background:'none', border:'none', cursor:'pointer',
      borderBottom: active ? '2px solid var(--sky)' : '2px solid transparent',
      color: active ? 'var(--sky)' : 'var(--silver)',
    }}>
      {label}
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)',
      textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:8 }}>
      {children}
    </div>
  );
}

function DocRow({ nombre, tipo, comentario, url, saved, pending, onDelete }) {
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10,
      padding:'8px 12px', background:'rgba(var(--steel-rgb),0.3)',
      borderRadius:6, marginBottom:6,
    }}>
      <FileText size={14} style={{ color: saved ? 'var(--sky-light)' : 'var(--silver)', flexShrink:0 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {nombre}
        </div>
        <div style={{ fontSize:11, color:'var(--silver)' }}>
          {tipo}{comentario ? ` · ${comentario}` : ''}
        </div>
      </div>
      {saved   && <span className="badge badge-success" style={{ fontSize:10 }}>guardado</span>}
      {pending && <span className="badge badge-info"    style={{ fontSize:10 }}>pendiente</span>}
      {url && saved && (
        <a href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(url); }}
          className="btn btn-icon btn-secondary btn-sm" title="Ver archivo" style={{ padding:4 }}>
          <ExternalLink size={12}/>
        </a>
      )}
      {onDelete && (
        <button className="btn btn-icon btn-danger btn-sm" onClick={onDelete} title="Eliminar" style={{ padding:4 }}>
          <Trash2 size={12}/>
        </button>
      )}
    </div>
  );
}

function InfoField({ label, value, style = {} }) {
  return (
    <div style={style}>
      <div className="text-xs text-silver" style={{ marginBottom:2, textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
      <div style={{ fontSize:14, fontWeight:500 }}>{value || '—'}</div>
    </div>
  );
}
